import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  computeDelta,
  formatCurrency,
  formatDateTime,
  formatPercent,
  formatRatingOver5,
  getFinalRatingOver5,
  getPreviousRatingOver5,
  getRecommendationStatusColor,
  getStatusColor,
  parseBeforeAfter,
  titleCase,
  toFiniteNumber,
} from "../report.utils";

function SectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 800, fontFamily: '"Sora","Segoe UI",sans-serif' }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function TimelineRow({ step, previousPercentage }) {
  const current = toFiniteNumber(step?.percentage);
  const delta =
    current !== null && previousPercentage !== null
      ? Math.round((current - previousPercentage) * 10) / 10
      : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: "8px",
        mb: 1.25,
        borderColor: "rgba(0,0,0,0.08)",
        bgcolor: "rgba(255,255,255,0.84)",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
        <Box>
          <Typography sx={{ fontWeight: 700 }}>{step?.step_name || "Step"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {step?.evaluator?.name || "Unassigned"} ({titleCase(step?.evaluator?.role)})
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={`% ${formatPercent(step?.percentage, 1)}`} />
          <Chip size="small" label={`Score ${step?.score ?? "-"}`} variant="outlined" />
          {delta !== null ? (
            <Chip
              size="small"
              label={`Delta ${delta > 0 ? "+" : ""}${delta}`}
              color={delta > 0 ? "success" : delta < 0 ? "error" : "default"}
              variant="outlined"
            />
          ) : null}
        </Stack>
      </Stack>

      {step?.submitted_at ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Updated: {formatDateTime(step?.submitted_at)}
        </Typography>
      ) : null}
    </Paper>
  );
}

function OverviewTab({ item }) {
  const delta = computeDelta(item);
  const latestRecommendation = useMemo(() => {
    const rows = Array.isArray(item?.recommendations) ? item.recommendations : [];
    return [...rows].sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))[0] || null;
  }, [item]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
        <SectionTitle title="Overview" />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Employee
            </Typography>
            <Typography>
              {item?.employee?.name || "-"} ({item?.employee?.employee_code || "-"})
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Department / Position
            </Typography>
            <Typography>
              {item?.employee?.department || "-"} / {item?.employee?.position || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Cycle / Project
            </Typography>
            <Typography>
              {item?.cycle_name || "-"} / {item?.project?.name || "-"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip
                size="small"
                label={titleCase(item?.status)}
                color={getStatusColor(item?.status)}
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Final Percentage
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>{formatPercent(item?.final_percentage, 1)}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Final Rating (/5)
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>{formatRatingOver5(getFinalRatingOver5(item))}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Previous Cycle / Delta
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>{formatPercent(item?.previous_cycle?.final_percentage, 1)}</Typography>
              {delta !== null ? (
                <Chip
                  size="small"
                  label={`${delta > 0 ? "+" : ""}${delta}`}
                  color={delta > 0 ? "success" : delta < 0 ? "error" : "default"}
                  variant="outlined"
                />
              ) : null}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Previous Rating (/5)
            </Typography>
            <Typography>{formatRatingOver5(getPreviousRatingOver5(item))}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Last Step
            </Typography>
            <Typography>{item?.last_step || "-"}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Updated At
            </Typography>
            <Typography>{formatDateTime(item?.last_updated_at)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
        <SectionTitle title="Latest Recommendation" />

        {!latestRecommendation ? (
          <Typography color="text.secondary">No recommendation found.</Typography>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip label={titleCase(latestRecommendation?.action_type)} size="small" />
              <Chip
                label={titleCase(latestRecommendation?.status)}
                size="small"
                color={getRecommendationStatusColor(latestRecommendation?.status)}
                variant="outlined"
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Step: {titleCase(latestRecommendation?.step_key)}
            </Typography>

            {latestRecommendation?.amount ? (
              <Typography variant="body2">Amount: {formatCurrency(latestRecommendation.amount)}</Typography>
            ) : null}

            {latestRecommendation?.new_position_id ? (
              <Typography variant="body2">New Position ID: {latestRecommendation.new_position_id}</Typography>
            ) : null}

            {latestRecommendation?.notes ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {latestRecommendation.notes}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

function StepsTimelineTab({ item }) {
  const rows = useMemo(
    () => (Array.isArray(item?.step_scores) ? item.step_scores : []),
    [item?.step_scores]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
      <SectionTitle
        title="Step Scores Timeline"
        subtitle="Step-by-step evaluator scores and deltas across workflow."
      />

      {rows.length === 0 ? (
        <Typography color="text.secondary">No step score records.</Typography>
      ) : (
        rows.map((step, idx) => {
          const prev = idx > 0 ? toFiniteNumber(rows[idx - 1]?.percentage) : null;
          return <TimelineRow key={`${step?.step_key || idx}-${idx}`} step={step} previousPercentage={prev} />;
        })
      )}
    </Paper>
  );
}

function RecommendationsTab({ item }) {
  const rows = useMemo(
    () =>
      [...(Array.isArray(item?.recommendations) ? item.recommendations : [])].sort(
        (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0)
      ),
    [item?.recommendations]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
      <SectionTitle
        title="Recommendations"
        subtitle="All evaluator recommendations ordered by creation time."
      />

      {rows.length === 0 ? (
        <Typography color="text.secondary">No recommendations.</Typography>
      ) : (
        rows.map((rec) => (
          <Paper
            key={rec?.id || `${rec?.action_type}-${rec?.created_at}`}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: "8px", mb: 1.25 }}
          >
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={titleCase(rec?.action_type)} size="small" />
                <Chip
                  label={titleCase(rec?.status)}
                  size="small"
                  color={getRecommendationStatusColor(rec?.status)}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {titleCase(rec?.step_key)}
                </Typography>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {formatDateTime(rec?.created_at)}
              </Typography>
            </Stack>

            {rec?.amount ? (
              <Typography variant="body2">Amount: {formatCurrency(rec.amount)}</Typography>
            ) : null}
            {rec?.percent ? <Typography variant="body2">Percent: {rec.percent}%</Typography> : null}
            {rec?.new_position_id ? (
              <Typography variant="body2">New Position ID: {rec.new_position_id}</Typography>
            ) : null}

            {rec?.notes ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                {rec.notes}
              </Typography>
            ) : null}
          </Paper>
        ))
      )}
    </Paper>
  );
}

function ChangesTab({ item }) {
  const rows = useMemo(
    () =>
      [...(Array.isArray(item?.changes) ? item.changes : [])].sort(
        (a, b) => new Date(b?.changed_at || 0) - new Date(a?.changed_at || 0)
      ),
    [item?.changes]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px" }}>
      <SectionTitle
        title="Changes Log"
        subtitle="Score/rating/recommendation changes with actor, timestamps, and reason."
      />

      {rows.length === 0 ? (
        <Typography color="text.secondary">No change events.</Typography>
      ) : (
        rows.map((change) => {
          const isScoreChange =
            String(change?.field || "").includes("percentage") ||
            String(change?.field || "").includes("rating") ||
            String(change?.field || "").includes("recommend");

          return (
            <Paper
              key={change?.id || `${change?.field}-${change?.changed_at}`}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: "8px",
                mb: 1.25,
                borderColor: isScoreChange ? "warning.main" : undefined,
                bgcolor: isScoreChange ? "rgba(255,167,38,0.08)" : "transparent",
              }}
            >
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                <Typography sx={{ fontWeight: 700 }}>{titleCase(change?.field)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(change?.changed_at)}
                </Typography>
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Changed by: {change?.changed_by?.name || "-"} ({titleCase(change?.changed_by?.role)})
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: 1.2 }}>
                <Box sx={{ flex: 1, p: 1, borderRadius: "8px", border: "1px solid #ef9a9a", bgcolor: "#ffebee" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Before
                  </Typography>
                  <Typography
                    component="pre"
                    sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {parseBeforeAfter(change?.before)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, p: 1, borderRadius: "8px", border: "1px solid #a5d6a7", bgcolor: "#e8f5e9" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    After
                  </Typography>
                  <Typography
                    component="pre"
                    sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {parseBeforeAfter(change?.after)}
                  </Typography>
                </Box>
              </Stack>

              {change?.reason ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Reason: {change.reason}
                </Typography>
              ) : null}
            </Paper>
          );
        })
      )}
    </Paper>
  );
}

export default function AppraisalDetailsDrawer({ open, onClose, item }) {
  const [tab, setTab] = useState(0);

  if (!item) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 680 },
          p: 2,
          background:
            "linear-gradient(170deg, rgba(255,255,255,0.98) 0%, rgba(247,250,255,0.98) 52%, rgba(238,247,255,0.95) 100%)",
          borderLeft: "1px solid rgba(0,0,0,0.08)",
        },
      }}
    >
      <Stack spacing={1} sx={{ mb: 1 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, fontFamily: '"Sora","Segoe UI",sans-serif' }}
        >
          Appraisal #{item?.id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {item?.employee?.name || "-"} ({item?.employee?.employee_code || "-"})
        </Typography>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Tabs
        value={tab}
        onChange={(_e, next) => setTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mb: 1.5,
          minHeight: 42,
          "& .MuiTab-root": {
            minHeight: 42,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            mr: 0.5,
          },
          "& .MuiTabs-indicator": {
            height: 3,
            borderRadius: "8px",
          },
        }}
      >
        <Tab label="Overview" />
        <Tab label="Step Scores" />
        <Tab label="Recommendations" />
        <Tab label="Changes" />
      </Tabs>

      <Box sx={{ overflowY: "auto", pr: 0.5 }}>
        {tab === 0 ? <OverviewTab item={item} /> : null}
        {tab === 1 ? <StepsTimelineTab item={item} /> : null}
        {tab === 2 ? <RecommendationsTab item={item} /> : null}
        {tab === 3 ? <ChangesTab item={item} /> : null}
      </Box>

      <Divider sx={{ mt: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Updated: {formatDateTime(item?.last_updated_at)}
      </Typography>
    </Drawer>
  );
}
